import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import UpgradeModal from '../UpgradeModal/UpgradeModal';
import UploadOverlay from '../UploadOverlay/UploadOverlay';
import './MyCollectionScreen.css';

const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const CONDITION_OPTIONS = ['Mint', 'Excellent', 'Very Good', 'Good', 'Fair', 'Poor', 'Project/Parts'];

const MyCollectionScreen = () => {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('owned');
  const [sortBy, setSortBy] = useState('date_added');
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // "I Own This" modal state (from wishlist)
  const [ownModalItem, setOwnModalItem] = useState(null);
  const [ownFormData, setOwnFormData] = useState({ acquisition_type: 'Purchased', serial_number: '', purchase_date: '', purchase_price: '', condition: '', notes: '' });
  const [ownFormSubmitting, setOwnFormSubmitting] = useState(false);
  // Photo upload
  const [uploadingPhotoFor, setUploadingPhotoFor] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoItems, setPhotoItems] = useState({}); // keyed by collection_id
  const [dragOverCard, setDragOverCard] = useState(null);
  const fileInputRef = useRef(null);
  // Visibility toggling
  const [togglingVisibility, setTogglingVisibility] = useState(null);
  // Contribute to database
  const [contributePending, setContributePending] = useState(null); // collection_id awaiting confirm
  const [contributing, setContributing] = useState(null); // collection_id in-flight
  // Thumbnail picker
  const [thumbnailPicker, setThumbnailPicker] = useState(null); // { collectionId, photos, preferred }
  const [thumbnailSaving, setThumbnailSaving] = useState(false);
  // Add Amp (search for parent & own directly)
  const [showAddAmpModal, setShowAddAmpModal] = useState(false);
  const [addAmpStep, setAddAmpStep] = useState(1); // 1 = search, 2 = own form, 3 = create custom
  const [addAmpSearch, setAddAmpSearch] = useState('');
  const [addAmpResults, setAddAmpResults] = useState([]);
  const [addAmpSearching, setAddAmpSearching] = useState(false);
  const [addAmpSelected, setAddAmpSelected] = useState(null); // selected parent amp
  const searchTimerRef = useRef(null);
  // Custom amp form state (step 3)
  const [customAmpForm, setCustomAmpForm] = useState({ manufacturer_name: '', manufacturer_id: null, amp_name: '', start_year: '', end_year: '', description: '' });
  const [mfrSuggestions, setMfrSuggestions] = useState([]);
  const [mfrSearching, setMfrSearching] = useState(false);
  const [showMfrSuggestions, setShowMfrSuggestions] = useState(false);
  const mfrTimerRef = useRef(null);
  // Valuation
  const [valuatingItem, setValuatingItem] = useState(null); // collection_id currently being AI-valued
  const [valuationModal, setValuationModal] = useState(null); // { item, history }
  const [valuationLoading, setValuationLoading] = useState(false);
  const [manualValueForm, setManualValueForm] = useState({ estimated_value: '', notes: '' });
  const [showManualForm, setShowManualForm] = useState(false);
  const [certificateDownloading, setCertificateDownloading] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  // Collection Sharing
  const [shareModal, setShareModal] = useState(false);
  const [shareSettings, setShareSettings] = useState(null); // { share_enabled, share_token, share_url }
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const fetchCollection = useCallback(async () => {
    if (!token) return;
    try {
      const isWishlist = activeTab === 'wishlist';
      const response = await fetch(
        `${API_URL}/collections/my?is_wishlist=${isWishlist}&sort=${sortBy}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setItems(data.items);
        setStats(data.stats);
        setError('');
        // Auto-switch to wishlist tab if user has 0 owned but has wishlist items (first load)
        if (activeTab === 'owned' && data.items.length === 0 && data.stats.total_wishlist > 0) {
          setActiveTab('wishlist');
          return; // useCallback will re-run with new tab
        }
      } else {
        const errData = await response.json();
        setError(errData.error || 'Failed to load collection');
      }
    } catch (err) {
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, sortBy]);

  useEffect(() => {
    fetchCollection();
  }, [fetchCollection]);

  // Eagerly fetch share settings when Showcase tab is selected
  useEffect(() => {
    if (activeTab === 'showcase' && !shareSettings && token) {
      fetchShareSettings();
    }
  }, [activeTab, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch photos for owned items
  useEffect(() => {
    if (activeTab !== 'owned' || !token) return;
    items.forEach(async (item) => {
      if (!item.is_wishlist && !photoItems[item.collection_id]) {
        try {
          const resp = await fetch(`${API_URL}/collections/${item.collection_id}/photos`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (resp.ok) {
            const photos = await resp.json();
            setPhotoItems(prev => ({ ...prev, [item.collection_id]: photos }));
          }
        } catch (e) { /* silent */ }
      }
    });
  }, [items, activeTab, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleDelete = async (collectionId, ampName) => {
    const isOwned = activeTab === 'owned';
    const message = isOwned 
      ? `Remove "${ampName}" from your collection? This will delete your private amp entry and all its photos.`
      : `Remove "${ampName}" from your wishlist?`;
    if (!window.confirm(message)) return;
    try {
      const response = await fetch(`${API_URL}/collections/${collectionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        showSuccess(`Removed "${ampName}"`);
        fetchCollection();
      }
    } catch (err) {
      setError('Failed to remove item');
    }
  };

  // "I Own This" from wishlist — opens modal
  const openOwnModal = (item) => {
    // Check free tier limit before opening the form
    if (stats && stats.is_at_limit) {
      setShowUpgradeModal(true);
      return;
    }
    setOwnModalItem(item);
    setOwnFormData({ acquisition_type: 'Purchased', serial_number: '', purchase_date: '', purchase_price: '', condition: '', notes: '' });
  };

  // "Add Amplifier" from My Collection — search for parent amp
  const openAddAmpModal = () => {
    // Check free tier limit before opening the form
    if (stats && stats.is_at_limit) {
      setShowUpgradeModal(true);
      return;
    }
    setShowAddAmpModal(true);
    setAddAmpStep(1);
    setAddAmpSearch('');
    setAddAmpResults([]);
    setAddAmpSelected(null);
    setOwnFormData({ acquisition_type: 'Purchased', serial_number: '', purchase_date: '', purchase_price: '', condition: '', notes: '' });
  };

  const closeAddAmpModal = () => {
    setShowAddAmpModal(false);
    setAddAmpSearch('');
    setAddAmpResults([]);
    setAddAmpSelected(null);
    setAddAmpStep(1);
    setCustomAmpForm({ manufacturer_name: '', manufacturer_id: null, amp_name: '', start_year: '', end_year: '', description: '' });
    setMfrSuggestions([]);
    setShowMfrSuggestions(false);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (mfrTimerRef.current) clearTimeout(mfrTimerRef.current);
  };

  const handleAddAmpSearch = (query) => {
    setAddAmpSearch(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query.trim() || query.trim().length < 2) {
      setAddAmpResults([]);
      setAddAmpSearching(false);
      return;
    }
    setAddAmpSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const resp = await fetch(`${API_URL}/amplifiers/search?q=${encodeURIComponent(query.trim())}&pageSize=15`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setAddAmpResults(data.data || []);
        }
      } catch (e) { /* silent */ }
      setAddAmpSearching(false);
    }, 350);
  };

  const selectParentAmp = (amp) => {
    setAddAmpSelected(amp);
    setAddAmpStep(2);
  };

  const submitAddAmp = async () => {
    if (!addAmpSelected || ownFormSubmitting) return;
    setOwnFormSubmitting(true);
    try {
      const resp = await fetch(`${API_URL}/collections/own`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_amplifier_id: addAmpSelected.amplifier_id,
          ...ownFormData
        })
      });
      if (resp.ok) {
        showSuccess(`Added "${addAmpSelected.manufacturer_name} ${addAmpSelected.model || addAmpSelected.name}" to your collection!`);
        closeAddAmpModal();
        setActiveTab('owned');
        fetchCollection();
      } else {
        const err = await resp.json();
        if (err.upgrade_required) {
          closeAddAmpModal();
          setShowUpgradeModal(true);
        } else {
          setError(err.error || 'Failed to add to collection');
        }
      }
    } catch (e) {
      setError('Failed to add to collection');
    }
    setOwnFormSubmitting(false);
  };

  // Manufacturer autocomplete for custom amp creation
  const handleMfrSearch = (query) => {
    setCustomAmpForm(prev => ({ ...prev, manufacturer_name: query, manufacturer_id: null }));
    if (mfrTimerRef.current) clearTimeout(mfrTimerRef.current);
    if (!query.trim() || query.trim().length < 2) {
      setMfrSuggestions([]);
      setShowMfrSuggestions(false);
      return;
    }
    setMfrSearching(true);
    mfrTimerRef.current = setTimeout(async () => {
      try {
        const resp = await fetch(`${API_URL}/manufacturers?manufacturer_name=${encodeURIComponent(query.trim())}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setMfrSuggestions(data.slice(0, 10));
          setShowMfrSuggestions(true);
        }
      } catch (e) { /* silent */ }
      setMfrSearching(false);
    }, 300);
  };

  const selectMfr = (mfr) => {
    setCustomAmpForm(prev => ({ ...prev, manufacturer_name: mfr.manufacturer_name, manufacturer_id: mfr.manufacturer_id }));
    setShowMfrSuggestions(false);
    setMfrSuggestions([]);
  };

  // Submit custom amp creation
  const submitCustomAmp = async () => {
    if (ownFormSubmitting) return;
    if (!customAmpForm.manufacturer_name.trim()) {
      setError('Manufacturer name is required');
      return;
    }
    if (!customAmpForm.amp_name.trim()) {
      setError('Amplifier name is required');
      return;
    }
    setOwnFormSubmitting(true);
    try {
      const resp = await fetch(`${API_URL}/collections/own-custom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          manufacturer_name: customAmpForm.manufacturer_name.trim(),
          manufacturer_id: customAmpForm.manufacturer_id,
          amp_name: customAmpForm.amp_name.trim(),
          start_year: customAmpForm.start_year ? parseInt(customAmpForm.start_year) : null,
          end_year: customAmpForm.end_year ? parseInt(customAmpForm.end_year) : null,
          description: customAmpForm.description || null,
          ...ownFormData
        })
      });
      if (resp.ok) {
        showSuccess(`Created "${customAmpForm.manufacturer_name.trim()} ${customAmpForm.amp_name.trim()}" and added to your collection!`);
        closeAddAmpModal();
        setActiveTab('owned');
        fetchCollection();
      } else {
        const err = await resp.json();
        if (err.upgrade_required) {
          closeAddAmpModal();
          setShowUpgradeModal(true);
        } else {
          setError(err.error || 'Failed to create amplifier');
        }
      }
    } catch (e) {
      setError('Failed to create amplifier');
    }
    setOwnFormSubmitting(false);
  };

  const submitOwnForm = async () => {
    if (!ownModalItem || ownFormSubmitting) return;
    setOwnFormSubmitting(true);
    try {
      const resp = await fetch(`${API_URL}/collections/own`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_amplifier_id: ownModalItem.amplifier_id,
          ...ownFormData
        })
      });
      if (resp.ok) {
        showSuccess(`Added "${getAmpDisplayName(ownModalItem)}" to your collection!`);
        setOwnModalItem(null);
        setActiveTab('owned');
        fetchCollection();
      } else {
        const err = await resp.json();
        if (err.upgrade_required) {
          setOwnModalItem(null);
          setShowUpgradeModal(true);
        } else {
          setError(err.error || 'Failed to add to collection');
        }
      }
    } catch (e) {
      setError('Failed to add to collection');
    }
    setOwnFormSubmitting(false);
  };

  // Edit owned item details
  const startEdit = (item) => {
    setEditingItem(item.collection_id);
    setEditForm({
      acquisition_type: item.acquisition_type || 'Purchased',
      purchase_date: item.purchase_date ? item.purchase_date.split('T')[0] : '',
      purchase_price: item.purchase_price || '',
      condition: item.condition || '',
      notes: item.notes || '',
      serial_number: item.collection_serial || item.serial_number || ''
    });
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditForm({});
  };

  const saveEdit = async (item) => {
    try {
      const response = await fetch(`${API_URL}/collections/${item.collection_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });
      if (response.ok) {
        setEditingItem(null);
        showSuccess('Updated successfully');
        fetchCollection();
      }
    } catch (err) {
      setError('Failed to update');
    }
  };

  // Toggle public/private
  const toggleVisibility = async (item) => {
    setTogglingVisibility(item.collection_id);
    try {
      const resp = await fetch(`${API_URL}/collections/${item.collection_id}/visibility`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_private: !item.is_private })
      });
      if (resp.ok) {
        const data = await resp.json();
        showSuccess(data.message);
        fetchCollection();
      } else {
        const errData = await resp.json().catch(() => ({}));
        setError(errData.message || errData.error || 'Failed to update visibility');
      }
    } catch (e) {
      setError('Failed to update visibility');
    }
    setTogglingVisibility(null);
  };

  // Contribute owned amp to the public database (irreversible)
  const contributeToDatabase = async (item) => {
    setContributing(item.collection_id);
    setContributePending(null);
    try {
      const resp = await fetch(`${API_URL}/collections/${item.collection_id}/contribute`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        showSuccess(data.message || 'Amp contributed to the database!');
        fetchCollection();
      } else {
        const errData = await resp.json().catch(() => ({}));
        setError(errData.error || 'Failed to contribute amp.');
      }
    } catch (e) {
      setError('Failed to contribute amp.');
    }
    setContributing(null);
  };

  // Photo upload — shared core logic used by both the file picker and drag-and-drop
  const uploadPhotos = async (files, collectionId) => {
    if (!files?.length || !collectionId) return;
    setPhotoUploading(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('photos', files[i]);
    }
    try {
      const resp = await fetch(`${API_URL}/collections/${collectionId}/photos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (resp.ok) {
        const data = await resp.json();
        const blockedCount = data.blocked?.length || 0;
        if (blockedCount > 0 && data.count > 0) {
          showSuccess(`Uploaded ${data.count} photo(s). ${blockedCount} image(s) were blocked for inappropriate content.`);
        } else {
          showSuccess(`Uploaded ${data.count} photo(s)`);
        }
        setPhotoItems(prev => ({ ...prev, [collectionId]: undefined }));
        fetchCollection();
      } else {
        const errData = await resp.json().catch(() => ({}));
        if (errData.code === 'IMAGE_MODERATION_BLOCKED') {
          setError(errData.error || 'Your image was blocked for inappropriate content.');
        } else {
          setError(errData.error || 'Failed to upload photos');
        }
      }
    } catch (e) {
      setError('Failed to upload photos');
    }
    setPhotoUploading(false);
  };

  const triggerPhotoUpload = (collectionId) => {
    setUploadingPhotoFor(collectionId);
    fileInputRef.current?.click();
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    const collectionId = uploadingPhotoFor;
    setUploadingPhotoFor(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    await uploadPhotos(files, collectionId);
  };

  const handleCardDragOver = (e, collectionId) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragOverCard !== collectionId) setDragOverCard(collectionId);
  };

  const handleCardDragLeave = (e, collectionId) => {
    // Only clear if leaving the card itself (not a child element)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverCard(null);
    }
  };

  const handleCardDrop = async (e, collectionId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCard(null);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (!files.length) {
      setError('Only image files can be uploaded.');
      return;
    }
    await uploadPhotos(files, collectionId);
  };

  const deletePhoto = async (artifactId, collectionId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      const resp = await fetch(`${API_URL}/collections/photos/${artifactId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        showSuccess('Photo deleted');
        setPhotoItems(prev => ({
          ...prev,
          [collectionId]: prev[collectionId]?.filter(p => p.artifact_id !== artifactId)
        }));
      }
    } catch (e) {
      setError('Failed to delete photo');
    }
  };

  // Thumbnail picker
  const openThumbnailPicker = async (collectionId) => {
    try {
      const resp = await fetch(`${API_URL}/collections/${collectionId}/available-thumbnails`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.photos.length === 0) {
          setError('No photos available to choose from');
          return;
        }
        setThumbnailPicker({ collectionId, photos: data.photos, preferred: data.preferred_thumbnail_id });
      } else {
        const errData = await resp.json().catch(() => ({}));
        setError(errData.error || `Failed to load photos (${resp.status})`);
      }
    } catch (e) {
      console.error('Thumbnail picker error:', e);
      setError('Failed to load photos');
    }
  };

  const setPreferredThumbnail = async (artifactId) => {
    if (!thumbnailPicker || thumbnailSaving) return;
    setThumbnailSaving(true);
    try {
      // If clicking the already-preferred one, clear it (revert to default)
      const newId = artifactId === thumbnailPicker.preferred ? null : artifactId;
      const resp = await fetch(`${API_URL}/collections/${thumbnailPicker.collectionId}/thumbnail`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ artifact_id: newId })
      });
      if (resp.ok) {
        showSuccess(newId ? 'Thumbnail updated!' : 'Thumbnail reset to default');
        setThumbnailPicker(null);
        fetchCollection(); // refresh to show new thumbnail
      }
    } catch (e) {
      setError('Failed to update thumbnail');
    }
    setThumbnailSaving(false);
  };

  // === Valuation Functions ===
  const requestAiValuation = async (collectionId) => {
    setValuatingItem(collectionId);
    try {
      const resp = await fetch(`${API_URL}/collections/${collectionId}/valuate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        showSuccess(`Valued at ${formatCurrency(data.estimated_value)} (${data.confidence} confidence)`);
        fetchCollection();
        // If valuation modal is open for this item, refresh it
        if (valuationModal?.item?.collection_id === collectionId) {
          openValuationModal(items.find(i => i.collection_id === collectionId) || valuationModal.item, data);
        }
      } else if (resp.status === 402) {
        // Free tier limit reached — show upgrade modal
        setShowUpgradeModal(true);
      } else {
        const err = await resp.json();
        setError(err.error || 'AI valuation failed');
      }
    } catch (e) {
      setError('Failed to connect to valuation service');
    }
    setValuatingItem(null);
  };

  const openValuationModal = async (item, freshValuation) => {
    setShowManualForm(false);
    setManualValueForm({ estimated_value: '', notes: '' });
    // Fetch history
    try {
      const resp = await fetch(`${API_URL}/collections/${item.collection_id}/valuation-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const history = resp.ok ? await resp.json() : [];
      setValuationModal({ item: { ...item, ...(freshValuation || {}) }, history });
    } catch (e) {
      setValuationModal({ item, history: [] });
    }
  };

  const submitManualValue = async () => {
    if (!valuationModal || !manualValueForm.estimated_value) return;
    setValuationLoading(true);
    try {
      const resp = await fetch(`${API_URL}/collections/${valuationModal.item.collection_id}/value`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(manualValueForm)
      });
      if (resp.ok) {
        showSuccess('Valuation saved');
        setShowManualForm(false);
        fetchCollection();
        // Refresh the modal
        openValuationModal(valuationModal.item);
      } else {
        const err = await resp.json();
        setError(err.error || 'Failed to save valuation');
      }
    } catch (e) {
      setError('Failed to save valuation');
    }
    setValuationLoading(false);
  };

  // Download AmpNebula Valuation™ Certificate (PDF)
  const downloadCertificate = async (collectionId) => {
    setCertificateDownloading(true);
    try {
      const resp = await fetch(`${API_URL}/collections/${collectionId}/valuation-certificate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Extract filename from Content-Disposition or use default
        const disposition = resp.headers.get('Content-Disposition');
        const filenameMatch = disposition && disposition.match(/filename="(.+)"/);
        a.download = filenameMatch ? filenameMatch[1] : `AmpNebula-Valuation-Certificate.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showSuccess('Certificate downloaded!');
      } else if (resp.status === 402) {
        setValuationModal(null);
        setShowUpgradeModal(true);
      } else {
        const err = await resp.json();
        setError(err.error || 'Failed to generate certificate');
      }
    } catch (e) {
      setError('Failed to download certificate');
    }
    setCertificateDownloading(false);
  };

  // Export entire owned collection as CSV or PDF
  const exportCollection = async (format) => {
    const setExporting = format === 'csv' ? setExportingCsv : setExportingPdf;
    setExporting(true);
    try {
      const resp = await fetch(`${API_URL}/collections/export/${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const disposition = resp.headers.get('Content-Disposition');
        const filenameMatch = disposition && disposition.match(/filename="(.+)"/);
        const dateStr = new Date().toISOString().split('T')[0];
        a.download = filenameMatch ? filenameMatch[1] : `AmpNebula-Collection-${dateStr}.${format}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showSuccess(`Collection exported as ${format.toUpperCase()}!`);
      } else if (resp.status === 402) {
        setShowUpgradeModal(true);
      } else {
        const err = await resp.json().catch(() => ({}));
        setError(err.error || `Failed to export collection as ${format.toUpperCase()}`);
      }
    } catch (e) {
      setError('Failed to export collection');
    }
    setExporting(false);
  };

  const getAmpDisplayName = (item) => {
    const name = item.amplifier_name || item.model || 'Unknown';
    return `${item.manufacturer_name} ${name}`;
  };

  const getYearDisplay = (item) => {
    if (item.start_year && item.end_year && item.start_year !== item.end_year) {
      return `${item.start_year}–${item.end_year}`;
    }
    if (item.start_year) return `${item.start_year}`;
    return null;
  };

  const getThumbnailUrl = (item) => {
    if (item.thumbnail_artifact_id) {
      return `${API_URL}/amplifier_artifacts/${item.thumbnail_artifact_id}`;
    }
    return null;
  };

  const getAmpLink = (item) => {
    // For wishlist items, link to the parent amp's public page
    if (item.is_wishlist) {
      return `/amps/${item.manufacturer_slug}/${item.amplifier_slug}`;
    }
    // Owned: link to the user's own child amp details
    return `/amplifier-components/${item.amplifier_id}`;
  };

  const getParentAmpLink = (item) => {
    if (item.parent_slug && item.manufacturer_slug) {
      return `/amps/${item.manufacturer_slug}/${item.parent_slug}`;
    }
    return null;
  };

  // ── Collection Sharing ────────────────────────────────────────────────────
  const fetchShareSettings = async () => {
    try {
      const resp = await fetch(`${API_URL}/collections/share/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setShareSettings(data);
      }
    } catch (e) { /* silent */ }
  };

  const openShareModal = async () => {
    setShareModal(true);
    setShareCopied(false);
    if (!shareSettings) {
      setShareLoading(true);
      await fetchShareSettings();
      setShareLoading(false);
    }
  };

  const toggleSharing = async (enable) => {
    setShareLoading(true);
    try {
      const endpoint = enable ? 'enable' : 'disable';
      const resp = await fetch(`${API_URL}/collections/share/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (resp.ok) {
        const data = await resp.json();
        setShareSettings(data);
        setShareCopied(false);
      }
    } catch (e) {
      console.error('Error toggling share:', e);
    } finally {
      setShareLoading(false);
    }
  };

  const regenerateShareLink = async () => {
    if (!window.confirm('This will invalidate your current share link. Anyone with the old link will no longer be able to see your collection. Continue?')) return;
    setShareLoading(true);
    try {
      const resp = await fetch(`${API_URL}/collections/share/regenerate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (resp.ok) {
        const data = await resp.json();
        setShareSettings(data);
        setShareCopied(false);
      }
    } catch (e) {
      console.error('Error regenerating share link:', e);
    } finally {
      setShareLoading(false);
    }
  };

  const getFullShareUrl = () => {
    if (!shareSettings?.share_url) return '';
    return `${window.location.origin}${shareSettings.share_url}`;
  };

  const copyShareLink = () => {
    const url = getFullShareUrl();
    if (url) {
      navigator.clipboard.writeText(url).then(() => {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 3000);
      });
    }
  };

  const shareToSocial = (platform) => {
    const url = encodeURIComponent(getFullShareUrl());
    const text = encodeURIComponent(`Check out my amp collection on AmpNebula! 🎸🔊`);
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      reddit: `https://reddit.com/submit?url=${url}&title=${encodeURIComponent('My Amp Collection — AmpNebula')}`,
      email: `mailto:?subject=${encodeURIComponent('Check out my amp collection!')}&body=${text}%0A%0A${url}`
    };
    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  const formatCurrency = (value) => {
    if (!value || parseFloat(value) === 0) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  if (!isAuthenticated) {
    return (
      <div className="mc-screen">
        <div className="mc-container">
          <div className="mc-login-prompt">
            <div className="mc-login-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </div>
            <h2>My Collection</h2>
            <p>Sign in to start tracking your amplifier collection.</p>
            <Link to="/login" className="mc-btn mc-btn-primary">Sign In</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mc-screen">
      <UploadOverlay
        visible={!!photoUploading}
        message="Uploading photos…"
        subtext="Scanning for content safety"
      />
      <div className="mc-container">
        {/* Hidden file input for photo uploads */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handlePhotoUpload}
        />

        {/* Header */}
        <div className="mc-header">
          <div>
            <h1 className="mc-title">My Collection</h1>
            <p className="mc-subtitle">{user?.nickname || user?.username}</p>
            {stats && stats.is_at_limit && (
              <button
                className="mc-upgrade-hint"
                onClick={() => setShowUpgradeModal(true)}
              >
                {stats.total_owned}/{stats.collection_limit} amps &mdash; <span className="mc-upgrade-hint-link">upgrade for unlimited</span>
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="mc-btn mc-btn-secondary" onClick={openShareModal} title="Share your collection">
              🔗 Share
            </button>
            <button className="mc-btn mc-btn-primary mc-btn-add" onClick={openAddAmpModal}>
              + Add Amplifier
            </button>
          </div>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="mc-stats-row">
            <div className="mc-stat">
              <span className="mc-stat-value">{stats.total_owned}</span>
              <span className="mc-stat-label">Owned</span>
            </div>
            <div className="mc-stat-divider" />
            <div className="mc-stat">
              <span className="mc-stat-value">{stats.total_wishlist}</span>
              <span className="mc-stat-label">Wishlist</span>
            </div>
            <div className="mc-stat-divider" />
            <div className="mc-stat">
              <span className="mc-stat-value">{formatCurrency(stats.total_value)}</span>
              <span className="mc-stat-label">Market Value</span>
            </div>
            {stats.total_cost_basis > 0 && stats.total_cost_basis !== stats.total_value && (
              <>
                <div className="mc-stat-divider" />
                <div className="mc-stat">
                  <span className="mc-stat-value">{formatCurrency(stats.total_cost_basis)}</span>
                  <span className="mc-stat-label">Cost Basis</span>
                </div>
              </>
            )}
            <div className="mc-stat-divider" />
            <div className="mc-stat">
              <span className="mc-stat-value">
                {stats.total_owned}{stats.collection_limit !== '∞' && <span className="mc-stat-limit">/{stats.collection_limit}</span>}
              </span>
              <span className="mc-stat-label">
                {stats.tier === 'free' ? 'Free Tier' : stats.tier === 'pro' ? 'Pro' : stats.tier === 'business' ? 'Business' : 'Owned'}
                {stats.is_at_limit && <span className="mc-limit-badge">Limit</span>}
              </span>
            </div>
            {!stats.unlimited_valuations && (
              <>
                <div className="mc-stat-divider" />
                <div className="mc-stat">
                  <span className="mc-stat-value">
                    {stats.ai_amps_valuated}<span className="mc-stat-limit">/{stats.collection_limit}</span>
                  </span>
                  <span className="mc-stat-label">AmpNebula Valuations™</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Messages */}
        {error && <div className="mc-message mc-message-error">{error}<button className="mc-message-dismiss" onClick={() => setError('')} title="Dismiss">&times;</button></div>}
        {successMessage && <div className="mc-message mc-message-success">{successMessage}<button className="mc-message-dismiss" onClick={() => setSuccessMessage('')} title="Dismiss">&times;</button></div>}

        {/* Toolbar */}
        <div className="mc-toolbar">
          <div className="mc-tabs">
            <button 
              className={`mc-tab ${activeTab === 'owned' ? 'mc-tab-active' : ''}`}
              onClick={() => setActiveTab('owned')}
            >
              Owned{stats ? ` (${stats.total_owned})` : ''}
            </button>
            <button 
              className={`mc-tab ${activeTab === 'wishlist' ? 'mc-tab-active' : ''}`}
              onClick={() => setActiveTab('wishlist')}
            >
              Wishlist{stats ? ` (${stats.total_wishlist})` : ''}
            </button>
            <button 
              className={`mc-tab ${activeTab === 'showcase' ? 'mc-tab-active' : ''}`}
              onClick={async () => {
                // Navigate directly to the showcase editor
                if (shareSettings?.share_url) {
                  navigate(`${shareSettings.share_url}/edit`);
                } else if (token) {
                  try {
                    const resp = await fetch(`${API_URL}/collections/share/settings`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (resp.ok) {
                      const data = await resp.json();
                      setShareSettings(data);
                      if (data.share_url) navigate(`${data.share_url}/edit`);
                    }
                  } catch (e) { /* silent */ }
                }
              }}
            >
              🎨 Showcase
            </button>
          </div>
          {activeTab !== 'showcase' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <select 
                className="mc-sort-select"
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date_added">Recent</option>
                <option value="name">Name</option>
                <option value="purchase_date">Purchase Date</option>
                <option value="value">Value</option>
              </select>
              {activeTab === 'owned' && stats?.total_owned > 0 && (
                <>
                  <button
                    className="mc-btn mc-btn-secondary mc-btn-sm"
                    onClick={() => exportCollection('csv')}
                    disabled={exportingCsv}
                    title={stats?.tier === 'free' ? 'Pro feature — upgrade to export' : 'Export collection as CSV'}
                  >
                    {exportingCsv ? '⏳' : '⬇'} CSV
                  </button>
                  <button
                    className="mc-btn mc-btn-secondary mc-btn-sm"
                    onClick={() => exportCollection('pdf')}
                    disabled={exportingPdf}
                    title={stats?.tier === 'free' ? 'Pro feature — upgrade to export' : 'Export collection as PDF report'}
                  >
                    {exportingPdf ? '⏳' : '⬇'} PDF
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="mc-loading">
            <div className="mc-spinner" />
            <p>Loading collection…</p>
          </div>
        ) : items.length === 0 ? (
          <div className="mc-empty">
            <svg className="mc-empty-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              {activeTab === 'wishlist' ? (
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              ) : (
                <>
                  <rect x="3" y="3" width="7" height="7" rx="1"/>
                  <rect x="14" y="3" width="7" height="7" rx="1"/>
                  <rect x="3" y="14" width="7" height="7" rx="1"/>
                  <rect x="14" y="14" width="7" height="7" rx="1"/>
                </>
              )}
            </svg>
            <h3>{activeTab === 'owned' ? 'No amplifiers yet' : 'Wishlist is empty'}</h3>
            <p>
              {activeTab === 'owned' 
                ? 'Add an amplifier you own, or browse the catalog and click "I Own This" on any amp.'
                : 'Hit the heart button on any amplifier to add it to your wishlist.'}
            </p>
            {activeTab === 'owned' ? (
              <div className="mc-empty-actions">
                <button className="mc-btn mc-btn-primary" onClick={openAddAmpModal}>
                  + Add Amplifier
                </button>
                <Link to="/amplifier_search" className="mc-btn mc-btn-ghost">
                  Browse Catalog
                </Link>
              </div>
            ) : (
              <Link to="/amplifier_search" className="mc-btn mc-btn-primary">
                Browse Amplifiers
              </Link>
            )}
          </div>
        ) : (
          <div className="mc-grid">
            {items.map(item => (
              <div
                key={item.collection_id}
                className={`mc-card${dragOverCard === item.collection_id ? ' mc-card-drag-over' : ''}`}
                onDragOver={!item.is_wishlist ? (e) => handleCardDragOver(e, item.collection_id) : undefined}
                onDragLeave={!item.is_wishlist ? (e) => handleCardDragLeave(e, item.collection_id) : undefined}
                onDrop={!item.is_wishlist ? (e) => handleCardDrop(e, item.collection_id) : undefined}
              >
                {/* Drag-and-drop overlay — owned items only */}
                {dragOverCard === item.collection_id && (
                  <div style={{
                    position: 'absolute', inset: 0, zIndex: 10,
                    background: 'rgba(37, 99, 235, 0.12)',
                    border: '2px dashed #2563eb',
                    borderRadius: 'inherit',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    pointerEvents: 'none',
                  }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="16 16 12 12 8 16"/>
                      <line x1="12" y1="12" x2="12" y2="21"/>
                      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                    </svg>
                    <span style={{ marginTop: '6px', fontSize: '0.8rem', fontWeight: '600', color: '#2563eb' }}>Drop to upload</span>
                  </div>
                )}
                {/* Image */}
                <div className="mc-card-img-wrap">
                  <Link to={getAmpLink(item)} className="mc-card-img" target="_blank" rel="noopener noreferrer">
                    {getThumbnailUrl(item) ? (
                      <img 
                        src={getThumbnailUrl(item)} 
                        alt={getAmpDisplayName(item)} 
                        loading="lazy"
                      />
                    ) : (
                      <div className="mc-card-no-img">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <polyline points="21 15 16 10 5 21"/>
                        </svg>
                      </div>
                    )}
                  </Link>
                  {/* Visibility / contributed badge for owned items */}
                  {!item.is_wishlist && (
                    <span className={`mc-visibility-badge ${item.is_contributed ? 'mc-badge-contributed' : item.is_private ? 'mc-badge-private' : 'mc-badge-public'}`}>
                      {item.is_contributed ? 'In Database' : item.is_private ? 'My Details Private' : 'My Details Shared'}
                    </span>
                  )}
                  {/* Thumbnail picker button */}
                  <button 
                    className="mc-thumb-pick-btn" 
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openThumbnailPicker(item.collection_id); }}
                    title="Choose cover photo"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                  </button>
                </div>

                {/* Body */}
                <div className="mc-card-body">
                  <div className="mc-card-mfr">{item.manufacturer_name}</div>
                  <Link to={getAmpLink(item)} className="mc-card-name" target="_blank" rel="noopener noreferrer">
                    {item.amplifier_name || item.model}
                  </Link>
                  {!item.is_wishlist && item.parent_name && getParentAmpLink(item) && (
                    <div style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                      <Link to={getParentAmpLink(item)} style={{ color: '#718096', textDecoration: 'none' }}
                        target="_blank" rel="noopener noreferrer"
                        onMouseEnter={(e) => e.target.style.color = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.color = '#718096'}
                      >
                        View {item.parent_name} specs →
                      </Link>
                    </div>
                  )}
                  {getYearDisplay(item) && (
                    <div className="mc-card-year">{getYearDisplay(item)}</div>
                  )}

                  {/* Edit Form (owned items only) */}
                  {editingItem === item.collection_id ? (
                    <div className="mc-edit-form">
                      <label>
                        How acquired?
                        <select value={editForm.acquisition_type}
                          onChange={(e) => setEditForm({...editForm, acquisition_type: e.target.value})}>
                          <option value="Purchased">Purchased</option>
                          <option value="Built">Built / DIY</option>
                          <option value="Gift">Gift</option>
                          <option value="Inherited">Inherited</option>
                          <option value="Trade">Trade</option>
                          <option value="Found">Found / Salvaged</option>
                          <option value="Other">Other</option>
                        </select>
                      </label>
                      <label>
                        Serial #
                        <input type="text" value={editForm.serial_number}
                          onChange={(e) => setEditForm({...editForm, serial_number: e.target.value})} />
                      </label>
                      <label>
                        {editForm.acquisition_type === 'Built' ? 'Date Completed' : editForm.acquisition_type === 'Purchased' ? 'Purchase Date' : 'Date Acquired'}
                        <input type="date" value={editForm.purchase_date}
                          onChange={(e) => setEditForm({...editForm, purchase_date: e.target.value})} />
                      </label>
                      {(editForm.acquisition_type === 'Purchased' || editForm.acquisition_type === 'Built' || editForm.acquisition_type === 'Trade') && (
                      <label>
                        {editForm.acquisition_type === 'Built' ? 'Build Cost' : editForm.acquisition_type === 'Trade' ? 'Est. Value' : 'Price'}
                        <input type="number" step="0.01" placeholder="0.00" value={editForm.purchase_price}
                          onChange={(e) => setEditForm({...editForm, purchase_price: e.target.value})} />
                      </label>
                      )}
                      <label>
                        Condition
                        <select value={editForm.condition}
                          onChange={(e) => setEditForm({...editForm, condition: e.target.value})}>
                          <option value="">—</option>
                          {CONDITION_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </label>
                      <label>
                        Notes
                        <textarea rows="2" value={editForm.notes}
                          onChange={(e) => setEditForm({...editForm, notes: e.target.value})} />
                      </label>
                      <div className="mc-edit-actions">
                        <button className="mc-btn mc-btn-primary mc-btn-sm" onClick={() => saveEdit(item)}>Save</button>
                        <button className="mc-btn mc-btn-ghost mc-btn-sm" onClick={cancelEdit}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Detail chips */}
                      <div className="mc-card-meta">
                        {item.acquisition_type && item.acquisition_type !== 'Purchased' && (
                          <span className="mc-chip mc-chip-acq">{item.acquisition_type === 'Built' ? '🔧 Built' : item.acquisition_type === 'Gift' ? '🎁 Gift' : item.acquisition_type === 'Inherited' ? 'Inherited' : item.acquisition_type === 'Trade' ? '🔄 Trade' : item.acquisition_type === 'Found' ? 'Found' : item.acquisition_type}</span>
                        )}
                        {item.condition && (
                          <span className={`mc-chip mc-condition-${item.condition.toLowerCase().replace(/[^a-z]/g, '')}`}>
                            {item.condition}
                          </span>
                        )}
                        {(item.collection_serial || item.amp_serial) && (
                          <span className="mc-chip">S/N {item.collection_serial || item.amp_serial}</span>
                        )}
                        {item.notes && (
                          <p className="mc-card-notes">{item.notes}</p>
                        )}
                      </div>

                      {/* Valuation section for owned items */}
                      {!item.is_wishlist && (
                        <div className="mc-valuation-section">
                          {item.estimated_value ? (
                            <div className="mc-valuation-display" onClick={() => openValuationModal(item)}>
                              <div className="mc-valuation-main">
                                <span className="mc-valuation-label">Market Value</span>
                                <span className="mc-valuation-amount">{formatCurrency(item.estimated_value)}</span>
                              </div>
                              {(item.estimated_value_low && item.estimated_value_high) && (
                                <span className="mc-valuation-range">
                                  {formatCurrency(item.estimated_value_low)} – {formatCurrency(item.estimated_value_high)}
                                </span>
                              )}
                              {item.purchase_price && parseFloat(item.purchase_price) !== parseFloat(item.estimated_value) && (
                                <span className="mc-valuation-cost-basis">
                                  Cost basis: {formatCurrency(item.purchase_price)}
                                </span>
                              )}
                              <span className="mc-valuation-meta">
                                {item.valuation_method === 'ai_estimate' ? 'AmpNebula Valuation™' : 'Manual'} · Click for details
                              </span>
                            </div>
                          ) : (
                            <div className="mc-valuation-empty">
                              {item.purchase_price && (
                                <span className="mc-valuation-cost-only">
                                  Cost basis: {formatCurrency(item.purchase_price)}
                                </span>
                              )}
                              <button 
                                  className="mc-btn mc-btn-sm mc-btn-valuation"
                                  onClick={() => requestAiValuation(item.collection_id)}
                                  disabled={valuatingItem === item.collection_id}
                                >
                                  {valuatingItem === item.collection_id ? 'Analyzing...' : 'Run AmpNebula Valuation'}
                                </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Photo strip for owned items */}
                      {!item.is_wishlist && photoItems[item.collection_id]?.length > 0 && (
                        <div className="mc-photo-strip">
                          {photoItems[item.collection_id].map(photo => (
                            <div key={photo.artifact_id} className="mc-photo-thumb">
                              <img
                                src={`${API_URL}/amplifier_artifacts/${photo.artifact_id}`}
                                alt={photo.filename}
                                loading="lazy"
                              />
                              <button
                                className="mc-photo-delete"
                                onClick={() => deletePhoto(photo.artifact_id, item.collection_id)}
                                title="Delete photo"
                              >×</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Actions */}
                {editingItem !== item.collection_id && (
                  <div className="mc-card-actions">
                    {activeTab === 'wishlist' ? (
                      <>
                        {/* "I Own This" button — the key action on wishlist items */}
                        <button className="mc-action mc-action-accent" onClick={() => openOwnModal(item)} title="I Own This — add to collection">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </button>
                        <button className="mc-action mc-action-danger" onClick={() => handleDelete(item.collection_id, getAmpDisplayName(item))} title="Remove from wishlist">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      </>
                    ) : (
                      <>
                        {/* Edit details */}
                        <button className="mc-action" onClick={() => startEdit(item)} title="Edit details">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        {/* Upload photos */}
                        <button className="mc-action" onClick={() => triggerPhotoUpload(item.collection_id)} title="Upload photos">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21 15 16 10 5 21"/>
                          </svg>
                        </button>
                        {/* Contribute to public database — only shown when not yet contributed */}
                        {!item.is_contributed && (
                          <button
                            className="mc-action mc-action-contribute"
                            onClick={() => setContributePending(item.collection_id)}
                            disabled={contributing === item.collection_id}
                            title="Contribute this amp to the AmpNebula public database (permanent)"
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 2L12 16M12 2L8 6M12 2L16 6"/>
                              <path d="M20 18H4a2 2 0 0 0 0 4h16a2 2 0 0 0 0-4z"/>
                            </svg>
                          </button>
                        )}
                        {/* Toggle public/private — only affects user's personal details, parent amp stays public */}
                        <button 
                          className={`mc-action ${item.is_private ? '' : 'mc-action-accent'}`}
                          onClick={() => toggleVisibility(item)} 
                          disabled={togglingVisibility === item.collection_id || item.is_contributed}
                          title={item.is_contributed ? 'Contributed amps are permanently public' : item.is_private ? 'Share my details (serial #, photos, notes) publicly' : 'Hide my personal details from public view'}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {item.is_private ? (
                              <>
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                              </>
                            ) : (
                              <>
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                              </>
                            )}
                          </svg>
                        </button>
                        {/* Delete */}
                        <button className="mc-action mc-action-danger" onClick={() => handleDelete(item.collection_id, getAmpDisplayName(item))} title="Remove from collection">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add Amplifier Modal */}
        {showAddAmpModal && (
          <div className="mc-overlay" onClick={closeAddAmpModal}>
            <div className="mc-modal mc-modal-wide" onClick={(e) => e.stopPropagation()}>
              <button className="mc-modal-close" onClick={closeAddAmpModal}>×</button>
              
              {addAmpStep === 1 ? (
                <>
                  <h2>Add Amplifier</h2>
                  <p className="mc-modal-desc">
                    Search for the amplifier model yours is based on. Start typing a manufacturer or model name.
                  </p>
                  <div className="mc-add-search-wrap">
                    <input
                      type="text"
                      className="mc-add-search-input"
                      placeholder="e.g. Marshall JCM800, Fender Twin Reverb..."
                      value={addAmpSearch}
                      onChange={(e) => handleAddAmpSearch(e.target.value)}
                      autoFocus
                    />
                    {addAmpSearching && <span className="mc-add-searching">Searching...</span>}
                  </div>
                  <div className="mc-add-results">
                    {addAmpResults.length > 0 ? (
                      addAmpResults.map(amp => (
                        <button
                          key={amp.amplifier_id}
                          className="mc-add-result-item"
                          onClick={() => selectParentAmp(amp)}
                        >
                          <div className="mc-add-result-info">
                            <span className="mc-add-result-mfr">{amp.manufacturer_name}</span>
                            <span className="mc-add-result-name">{amp.model || amp.name}</span>
                          </div>
                          <span className="mc-add-result-years">
                            {amp.start_year ? `${amp.start_year}${amp.end_year ? `–${amp.end_year}` : '+'}` : ''}
                          </span>
                        </button>
                      ))
                    ) : addAmpSearch.length >= 2 && !addAmpSearching ? (
                      <div className="mc-add-no-results">
                        <p>No amplifiers found for "{addAmpSearch}"</p>
                        <p className="mc-add-no-results-hint">Try a different search, or create a new entry from scratch.</p>
                      </div>
                    ) : (
                      <div className="mc-add-hint">
                        <p>Type at least 2 characters to search the catalog</p>
                      </div>
                    )}
                  </div>
                  <div style={{ borderTop: '1px solid var(--mc-border)', paddingTop: '1rem', marginTop: '1rem', textAlign: 'center' }}>
                    <button
                      className="mc-btn mc-btn-ghost"
                      onClick={() => {
                        setAddAmpStep(3);
                        setOwnFormData({ acquisition_type: 'Purchased', serial_number: '', purchase_date: '', purchase_price: '', condition: '', notes: '' });
                      }}
                      style={{ fontSize: '0.9rem' }}
                    >
                      Can't find it? <strong>Create from scratch</strong>
                    </button>
                  </div>
                </>
              ) : addAmpStep === 2 ? (
                <>
                  <h2>Add to Collection</h2>
                  <div className="mc-add-selected-parent">
                    <div className="mc-add-selected-info">
                      <span className="mc-add-selected-mfr">{addAmpSelected.manufacturer_name}</span>
                      <span className="mc-add-selected-name">{addAmpSelected.model || addAmpSelected.name}</span>
                      {addAmpSelected.start_year && (
                        <span className="mc-add-selected-years">
                          {addAmpSelected.start_year}{addAmpSelected.end_year ? `–${addAmpSelected.end_year}` : '+'}
                        </span>
                      )}
                    </div>
                    <button className="mc-btn mc-btn-ghost mc-btn-sm" onClick={() => setAddAmpStep(1)}>
                      Change
                    </button>
                  </div>
                  <p className="mc-modal-desc">
                    This creates your own private entry as an instance of this amplifier.
                    You can add photos and share it publicly later.
                  </p>
                  <div className="mc-own-form">
                    <label>
                      How did you get this amp?
                      <select value={ownFormData.acquisition_type}
                        onChange={e => setOwnFormData(p => ({ ...p, acquisition_type: e.target.value }))}>
                        <option value="Purchased">Purchased</option>
                        <option value="Built">Built / DIY</option>
                        <option value="Gift">Gift</option>
                        <option value="Inherited">Inherited</option>
                        <option value="Trade">Trade</option>
                        <option value="Found">Found / Salvaged</option>
                        <option value="Other">Other</option>
                      </select>
                    </label>
                    <label>
                      Serial Number
                      <input type="text" value={ownFormData.serial_number} placeholder="e.g. AB12345"
                        onChange={e => setOwnFormData(p => ({ ...p, serial_number: e.target.value }))} />
                    </label>
                    <div className="mc-own-form-row">
                      <label>
                        {ownFormData.acquisition_type === 'Built' ? 'Date Completed' : ownFormData.acquisition_type === 'Purchased' ? 'Purchase Date' : 'Date Acquired'}
                        <input type="date" value={ownFormData.purchase_date}
                          onChange={e => setOwnFormData(p => ({ ...p, purchase_date: e.target.value }))} />
                      </label>
                      {(ownFormData.acquisition_type === 'Purchased' || ownFormData.acquisition_type === 'Built' || ownFormData.acquisition_type === 'Trade') && (
                      <label>
                        {ownFormData.acquisition_type === 'Built' ? 'Build Cost ($)' : ownFormData.acquisition_type === 'Trade' ? 'Est. Value ($)' : 'Purchase Price ($)'}
                        <input type="number" step="0.01" value={ownFormData.purchase_price} placeholder="0.00"
                          onChange={e => setOwnFormData(p => ({ ...p, purchase_price: e.target.value }))} />
                      </label>
                      )}
                    </div>
                    <label>
                      Condition
                      <select value={ownFormData.condition}
                        onChange={e => setOwnFormData(p => ({ ...p, condition: e.target.value }))}>
                        <option value="">Select condition...</option>
                        {CONDITION_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </label>
                    <label>
                      Notes
                      <textarea rows={3} value={ownFormData.notes} placeholder="Any details about your amp..."
                        onChange={e => setOwnFormData(p => ({ ...p, notes: e.target.value }))} />
                    </label>
                  </div>
                  <div className="mc-modal-actions">
                    <button className="mc-btn mc-btn-ghost" onClick={closeAddAmpModal}>Cancel</button>
                    <button className="mc-btn mc-btn-primary" onClick={submitAddAmp} disabled={ownFormSubmitting}>
                      {ownFormSubmitting ? 'Adding...' : 'Add to My Collection'}
                    </button>
                  </div>
                </>
              ) : (
                /* Step 3: Create from scratch */
                <>
                  <h2>Create New Amplifier</h2>
                  <p className="mc-modal-desc">
                    Enter the details for an amplifier that isn't in the catalog yet.
                    This creates a private entry in your collection.
                  </p>

                  <div className="mc-own-form">
                    {/* Manufacturer with autocomplete */}
                    <label style={{ position: 'relative' }}>
                      Manufacturer <span style={{ color: '#e74c3c' }}>*</span>
                      <input
                        type="text"
                        value={customAmpForm.manufacturer_name}
                        placeholder="e.g. Marshall, Ceriatone, DIY..."
                        onChange={e => handleMfrSearch(e.target.value)}
                        onFocus={() => { if (mfrSuggestions.length > 0) setShowMfrSuggestions(true); }}
                        onBlur={() => setTimeout(() => setShowMfrSuggestions(false), 200)}
                        autoFocus
                      />
                      {customAmpForm.manufacturer_id && (
                        <span style={{ fontSize: '0.75rem', color: '#3fb950', marginTop: '2px', display: 'block' }}>
                          ✓ Matched to existing manufacturer
                        </span>
                      )}
                      {customAmpForm.manufacturer_name.trim() && !customAmpForm.manufacturer_id && !mfrSearching && mfrSuggestions.length === 0 && customAmpForm.manufacturer_name.length >= 2 && (
                        <span style={{ fontSize: '0.75rem', color: '#f0ad4e', marginTop: '2px', display: 'block' }}>
                          New manufacturer — will be created automatically
                        </span>
                      )}
                      {showMfrSuggestions && mfrSuggestions.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'var(--mc-card-bg, #1e1e2e)',
                          border: '1px solid var(--mc-border, #333)',
                          borderRadius: '6px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 100,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                        }}>
                          {mfrSuggestions.map(mfr => (
                            <button
                              key={mfr.manufacturer_id}
                              type="button"
                              onMouseDown={(e) => { e.preventDefault(); selectMfr(mfr); }}
                              style={{
                                display: 'block',
                                width: '100%',
                                padding: '0.5rem 0.75rem',
                                border: 'none',
                                background: 'transparent',
                                color: 'inherit',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                borderBottom: '1px solid var(--mc-border, #333)'
                              }}
                            >
                              {mfr.manufacturer_name}
                            </button>
                          ))}
                        </div>
                      )}
                    </label>

                    <label>
                      Amplifier Name / Model <span style={{ color: '#e74c3c' }}>*</span>
                      <input
                        type="text"
                        value={customAmpForm.amp_name}
                        placeholder="e.g. JCM800 2204, OTS Mini 20..."
                        onChange={e => setCustomAmpForm(p => ({ ...p, amp_name: e.target.value }))}
                      />
                    </label>

                    <div className="mc-own-form-row">
                      <label>
                        Start Year
                        <input type="number" min="1900" max="2099" value={customAmpForm.start_year} placeholder="e.g. 2024"
                          onChange={e => setCustomAmpForm(p => ({ ...p, start_year: e.target.value }))} />
                      </label>
                      <label>
                        End Year
                        <input type="number" min="1900" max="2099" value={customAmpForm.end_year} placeholder="Leave blank if current"
                          onChange={e => setCustomAmpForm(p => ({ ...p, end_year: e.target.value }))} />
                      </label>
                    </div>

                    <label>
                      Description
                      <textarea rows={3} value={customAmpForm.description} placeholder="Brief description of the amp (circuit type, wattage, features, etc.)"
                        onChange={e => setCustomAmpForm(p => ({ ...p, description: e.target.value }))} />
                    </label>

                    <div style={{ borderTop: '1px solid var(--mc-border)', paddingTop: '1rem', marginTop: '0.5rem' }}>
                      <p style={{ fontSize: '0.85rem', color: 'var(--mc-text-muted)', margin: '0 0 0.75rem 0', fontWeight: '600' }}>
                        Ownership Details
                      </p>
                    </div>

                    <label>
                      How did you get this amp?
                      <select value={ownFormData.acquisition_type}
                        onChange={e => setOwnFormData(p => ({ ...p, acquisition_type: e.target.value }))}>
                        <option value="Purchased">Purchased</option>
                        <option value="Built">Built / DIY</option>
                        <option value="Gift">Gift</option>
                        <option value="Inherited">Inherited</option>
                        <option value="Trade">Trade</option>
                        <option value="Found">Found / Salvaged</option>
                        <option value="Other">Other</option>
                      </select>
                    </label>
                    <label>
                      Serial Number
                      <input type="text" value={ownFormData.serial_number} placeholder="e.g. AB12345"
                        onChange={e => setOwnFormData(p => ({ ...p, serial_number: e.target.value }))} />
                    </label>
                    <div className="mc-own-form-row">
                      <label>
                        {ownFormData.acquisition_type === 'Built' ? 'Date Completed' : ownFormData.acquisition_type === 'Purchased' ? 'Purchase Date' : 'Date Acquired'}
                        <input type="date" value={ownFormData.purchase_date}
                          onChange={e => setOwnFormData(p => ({ ...p, purchase_date: e.target.value }))} />
                      </label>
                      {(ownFormData.acquisition_type === 'Purchased' || ownFormData.acquisition_type === 'Built' || ownFormData.acquisition_type === 'Trade') && (
                      <label>
                        {ownFormData.acquisition_type === 'Built' ? 'Build Cost ($)' : ownFormData.acquisition_type === 'Trade' ? 'Est. Value ($)' : 'Purchase Price ($)'}
                        <input type="number" step="0.01" value={ownFormData.purchase_price} placeholder="0.00"
                          onChange={e => setOwnFormData(p => ({ ...p, purchase_price: e.target.value }))} />
                      </label>
                      )}
                    </div>
                    <label>
                      Condition
                      <select value={ownFormData.condition}
                        onChange={e => setOwnFormData(p => ({ ...p, condition: e.target.value }))}>
                        <option value="">Select condition...</option>
                        {CONDITION_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </label>
                    <label>
                      Notes
                      <textarea rows={3} value={ownFormData.notes} placeholder="Any details about your amp..."
                        onChange={e => setOwnFormData(p => ({ ...p, notes: e.target.value }))} />
                    </label>
                  </div>
                  <div className="mc-modal-actions">
                    <button className="mc-btn mc-btn-ghost" onClick={() => setAddAmpStep(1)}>← Back to Search</button>
                    <button
                      className="mc-btn mc-btn-primary"
                      onClick={submitCustomAmp}
                      disabled={ownFormSubmitting || !customAmpForm.manufacturer_name.trim() || !customAmpForm.amp_name.trim()}
                    >
                      {ownFormSubmitting ? 'Creating...' : 'Create & Add to Collection'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* "I Own This" Modal */}
        {ownModalItem && (
          <div className="mc-overlay" onClick={() => setOwnModalItem(null)}>
            <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
              <button className="mc-modal-close" onClick={() => setOwnModalItem(null)}>×</button>
              <h2>I Own This Amp</h2>
              <p className="mc-modal-desc">
                Add <strong>{getAmpDisplayName(ownModalItem)}</strong> to your collection.
                This creates your own private entry — you can upload photos and make it public later.
              </p>
              <div className="mc-own-form">
                <label>
                  How did you get this amp?
                  <select value={ownFormData.acquisition_type}
                    onChange={e => setOwnFormData(p => ({ ...p, acquisition_type: e.target.value }))}>
                    <option value="Purchased">Purchased</option>
                    <option value="Built">Built / DIY</option>
                    <option value="Gift">Gift</option>
                    <option value="Inherited">Inherited</option>
                    <option value="Trade">Trade</option>
                    <option value="Found">Found / Salvaged</option>
                    <option value="Other">Other</option>
                  </select>
                </label>
                <label>
                  Serial Number
                  <input type="text" value={ownFormData.serial_number} placeholder="e.g. AB12345"
                    onChange={e => setOwnFormData(p => ({ ...p, serial_number: e.target.value }))} />
                </label>
                <div className="mc-own-form-row">
                  <label>
                    {ownFormData.acquisition_type === 'Built' ? 'Date Completed' : ownFormData.acquisition_type === 'Purchased' ? 'Purchase Date' : 'Date Acquired'}
                    <input type="date" value={ownFormData.purchase_date}
                      onChange={e => setOwnFormData(p => ({ ...p, purchase_date: e.target.value }))} />
                  </label>
                  {(ownFormData.acquisition_type === 'Purchased' || ownFormData.acquisition_type === 'Built' || ownFormData.acquisition_type === 'Trade') && (
                  <label>
                    {ownFormData.acquisition_type === 'Built' ? 'Build Cost ($)' : ownFormData.acquisition_type === 'Trade' ? 'Est. Value ($)' : 'Purchase Price ($)'}
                    <input type="number" step="0.01" value={ownFormData.purchase_price} placeholder="0.00"
                      onChange={e => setOwnFormData(p => ({ ...p, purchase_price: e.target.value }))} />
                  </label>
                  )}
                </div>
                <label>
                  Condition
                  <select value={ownFormData.condition}
                    onChange={e => setOwnFormData(p => ({ ...p, condition: e.target.value }))}>
                    <option value="">Select condition...</option>
                    {CONDITION_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label>
                  Notes
                  <textarea rows={3} value={ownFormData.notes} placeholder="Any details about your amp..."
                    onChange={e => setOwnFormData(p => ({ ...p, notes: e.target.value }))} />
                </label>
              </div>
              <div className="mc-modal-actions">
                <button className="mc-btn mc-btn-ghost" onClick={() => setOwnModalItem(null)}>Cancel</button>
                <button className="mc-btn mc-btn-primary" onClick={submitOwnForm} disabled={ownFormSubmitting}>
                  {ownFormSubmitting ? 'Adding...' : 'Add to My Collection'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Modal */}
        <UpgradeModal
          show={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          message={stats && stats.is_at_limit
            ? `You've reached your ${stats.collection_limit}-amp free tier limit. Upgrade to Pro for unlimited collection, unlimited valuations, PDF reports, and more.`
            : 'Unlock the full AmpNebula experience with Pro:'}
        />

        {/* Thumbnail Picker Modal */}
        {thumbnailPicker && (
          <div className="mc-overlay" onClick={() => setThumbnailPicker(null)}>
            <div className="mc-modal mc-modal-wide" onClick={(e) => e.stopPropagation()}>
              <button className="mc-modal-close" onClick={() => setThumbnailPicker(null)}>×</button>
              <h2>Choose Cover Photo</h2>
              <p className="mc-modal-desc">
                Select which photo to use as the card thumbnail. Click the current selection again to reset to default.
              </p>
              <div className="mc-thumb-grid">
                {thumbnailPicker.photos.map(photo => (
                  <button
                    key={photo.artifact_id}
                    className={`mc-thumb-option ${photo.artifact_id === thumbnailPicker.preferred ? 'mc-thumb-selected' : ''}`}
                    onClick={() => setPreferredThumbnail(photo.artifact_id)}
                    disabled={thumbnailSaving}
                  >
                    <img
                      src={`${API_URL}/amplifier_artifacts/${photo.artifact_id}`}
                      alt={photo.filename}
                      loading="lazy"
                    />
                    {photo.artifact_id === thumbnailPicker.preferred && (
                      <span className="mc-thumb-check">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Valuation Detail Modal */}
        {valuationModal && (
          <div className="mc-overlay" onClick={() => setValuationModal(null)}>
            <div className="mc-modal mc-modal-wide" onClick={(e) => e.stopPropagation()}>
              <button className="mc-modal-close" onClick={() => setValuationModal(null)}>×</button>
              <h2>AmpNebula Valuation™</h2>
              <p className="mc-modal-desc">{getAmpDisplayName(valuationModal.item)}</p>

              {/* Current Value Summary */}
              {valuationModal.item.estimated_value && (
                <div className="mc-val-summary">
                  <div className="mc-val-main-row">
                    <div className="mc-val-primary">
                      <span className="mc-val-primary-label">Fair Market Value</span>
                      <span className="mc-val-primary-amount">{formatCurrency(valuationModal.item.estimated_value)}</span>
                    </div>
                    {valuationModal.item.trade_in_value && (
                      <div className="mc-val-secondary">
                        <span className="mc-val-secondary-label">Trade-in Value</span>
                        <span className="mc-val-secondary-amount">{formatCurrency(valuationModal.item.trade_in_value)}</span>
                      </div>
                    )}
                    {valuationModal.item.insurance_value && (
                      <div className="mc-val-secondary">
                        <span className="mc-val-secondary-label">Insurance / Replacement</span>
                        <span className="mc-val-secondary-amount">{formatCurrency(valuationModal.item.insurance_value)}</span>
                      </div>
                    )}
                  </div>
                  {(valuationModal.item.estimated_value_low && valuationModal.item.estimated_value_high) && (
                    <div className="mc-val-range-bar">
                      <span className="mc-val-range-label">{formatCurrency(valuationModal.item.estimated_value_low)}</span>
                      <div className="mc-val-range-track">
                        <div className="mc-val-range-fill" style={{
                          left: '0%',
                          width: `${Math.min(100, ((parseFloat(valuationModal.item.estimated_value) - parseFloat(valuationModal.item.estimated_value_low)) / (parseFloat(valuationModal.item.estimated_value_high) - parseFloat(valuationModal.item.estimated_value_low))) * 100)}%`
                        }} />
                      </div>
                      <span className="mc-val-range-label">{formatCurrency(valuationModal.item.estimated_value_high)}</span>
                    </div>
                  )}
                  {valuationModal.item.purchase_price && (
                    <div className="mc-val-cost-basis">
                      Cost Basis: {formatCurrency(valuationModal.item.purchase_price)}
                      {parseFloat(valuationModal.item.estimated_value) > parseFloat(valuationModal.item.purchase_price) && (
                        <span className="mc-val-gain">
                          +{formatCurrency(parseFloat(valuationModal.item.estimated_value) - parseFloat(valuationModal.item.purchase_price))} gain
                        </span>
                      )}
                    </div>
                  )}
                  {valuationModal.item.reverb_price_guide && (
                    <div className="mc-val-reverb-ref" style={{ margin: '12px 0', padding: '10px 14px', background: 'rgba(0,200,255,0.06)', border: '1px solid rgba(0,200,255,0.15)', borderRadius: 8, fontSize: '0.92rem' }}>
                      <strong style={{ color: '#00c8ff' }}>Reverb Price Guide Reference:</strong>{' '}
                      {valuationModal.item.reverb_price_guide.title} — ${valuationModal.item.reverb_price_guide.estimatedLow} – ${valuationModal.item.reverb_price_guide.estimatedHigh}
                      {valuationModal.item.reverb_price_guide.webUrl && (
                        <a href={valuationModal.item.reverb_price_guide.webUrl} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8, color: '#00c8ff', fontSize: '0.85rem' }}>View on Reverb ↗</a>
                      )}
                    </div>
                  )}
                  {valuationModal.item.valuation_notes && (
                    <div className="mc-val-reasoning">
                      {valuationModal.item.valuation_notes.split('\n').map((line, i) => (
                        <p key={i}>{line}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="mc-val-actions">
                {stats?.unlimited_valuations || !valuationModal.item.estimated_value ? (
                  <button 
                    className="mc-btn mc-btn-primary mc-btn-sm"
                    onClick={() => requestAiValuation(valuationModal.item.collection_id)}
                    disabled={valuatingItem === valuationModal.item.collection_id}
                  >
                    {valuatingItem === valuationModal.item.collection_id ? 'Analyzing...' : valuationModal.item.estimated_value ? 'Re-run AmpNebula Valuation' : 'Run AmpNebula Valuation'}
                  </button>
                ) : (
                  <button 
                    className="mc-btn mc-btn-primary mc-btn-sm"
                    onClick={() => { setValuationModal(null); setShowUpgradeModal(true); }}
                  >
                    ⭐ Upgrade to Re-run Valuations
                  </button>
                )}
                {valuationModal.item.estimated_value && (
                  <button 
                    className="mc-btn mc-btn-certificate mc-btn-sm"
                    onClick={() => downloadCertificate(valuationModal.item.collection_id)}
                    disabled={certificateDownloading}
                    title={stats?.tier === 'free' ? 'Pro feature — upgrade to download' : 'Download PDF certificate'}
                  >
                    {certificateDownloading ? '⏳ Generating...' : '📄 Download Certificate'}
                  </button>
                )}
                <button 
                  className="mc-btn mc-btn-ghost mc-btn-sm"
                  onClick={() => {
                    setShowManualForm(!showManualForm);
                    setManualValueForm({ 
                      estimated_value: valuationModal.item.estimated_value || '', 
                      notes: '' 
                    });
                  }}
                >
                  {showManualForm ? 'Cancel' : 'Set Manual Value'}
                </button>
              </div>

              {/* Manual Value Form */}
              {showManualForm && (
                <div className="mc-val-manual-form">
                  <label>
                    Estimated Market Value ($)
                    <input 
                      type="number" step="0.01" placeholder="0.00"
                      value={manualValueForm.estimated_value}
                      onChange={(e) => setManualValueForm(p => ({ ...p, estimated_value: e.target.value }))}
                    />
                  </label>
                  <label>
                    Notes (optional)
                    <textarea 
                      rows={2} placeholder="e.g., Based on recent eBay sold listings..."
                      value={manualValueForm.notes}
                      onChange={(e) => setManualValueForm(p => ({ ...p, notes: e.target.value }))}
                    />
                  </label>
                  <button 
                    className="mc-btn mc-btn-primary mc-btn-sm"
                    onClick={submitManualValue}
                    disabled={valuationLoading || !manualValueForm.estimated_value}
                  >
                    {valuationLoading ? 'Saving...' : 'Save Value'}
                  </button>
                </div>
              )}

              {/* Valuation History */}
              {valuationModal.history.length > 0 && (
                <div className="mc-val-history">
                  <h3>Valuation History</h3>
                  <div className="mc-val-history-list">
                    {valuationModal.history.map((entry) => (
                      <div key={entry.history_id} className="mc-val-history-item">
                        <div className="mc-val-history-row">
                          <span className="mc-val-history-amount">{formatCurrency(entry.estimated_value)}</span>
                          <span className="mc-val-history-method">
                            {entry.valuation_method === 'ai_estimate' ? 'AmpNebula' : 'Manual'}
                          </span>
                          <span className="mc-val-history-date">
                            {new Date(entry.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {entry.estimated_value_low && entry.estimated_value_high && (
                          <span className="mc-val-history-range">
                            Range: {formatCurrency(entry.estimated_value_low)} – {formatCurrency(entry.estimated_value_high)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Contribute to Database Confirmation Modal ──────────────── */}
        {contributePending && (() => {
          const item = items.find(c => c.collection_id === contributePending);
          return (
            <div className="mc-overlay" onClick={() => setContributePending(null)}>
              <div className="mc-modal" onClick={e => e.stopPropagation()}>
                <button className="mc-modal-close" onClick={() => setContributePending(null)}>×</button>
                <h2>Thank You for Contributing!</h2>
                <p className="mc-modal-desc">
                  You're about to share <strong>{item ? (item.amplifier_name || item.model) : 'this amp'}</strong> with the entire AmpNebula community — and that means a lot.
                </p>
                <p style={{ fontSize: '0.9rem', color: '#444', marginBottom: '1rem', lineHeight: '1.6' }}>
                  Real-world data from owners like you is the most valuable information we have. Every serial number, photo, and note you share helps collectors, technicians, and enthusiasts around the world better understand these instruments. You're helping build something genuinely useful.
                </p>
                <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.75rem', fontWeight: 500 }}>Just a few things to know before you confirm:</p>
                <ul style={{ fontSize: '0.85rem', color: '#555', marginBottom: '1.2rem', paddingLeft: '1.2rem', lineHeight: '1.7' }}>
                  <li>Your photos, serial number, and notes will be publicly visible (your AmpNebula valuation will not).</li>
                  <li>The amp will remain in the database even if you remove it from your collection or delete your account.</li>
                  <li><strong>This action cannot be undone.</strong> Contact support if you need to retract a contribution.</li>
                </ul>
                <div className="mc-edit-actions">
                  <button
                    className="mc-btn mc-btn-primary"
                    onClick={() => item && contributeToDatabase(item)}
                    disabled={contributing === contributePending}
                  >
                    {contributing === contributePending ? 'Contributing…' : 'Yes, Share with the Community'}
                  </button>
                  <button className="mc-btn mc-btn-ghost" onClick={() => setContributePending(null)}>Cancel</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Share Collection Modal ──────────────────────────────────── */}
        {shareModal && (
          <div className="mc-overlay" onClick={() => setShareModal(false)}>
            <div className="mc-modal mc-share-modal" onClick={e => e.stopPropagation()}>
              <div className="mc-modal-header">
                <h2>🔗 Share My Collection</h2>
                <button className="mc-modal-close" onClick={() => setShareModal(false)}>✕</button>
              </div>
              <div className="mc-modal-body">
                {shareLoading && !shareSettings ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Loading…</div>
                ) : (
                  <>
                    <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '1rem' }}>
                      Share a public link to your collection. Visitors can see your amps, photos, and condition — but <strong>valuations, prices, and insurance data are always private</strong>.
                    </p>

                    {/* Enable / Disable toggle */}
                    <div className="mc-share-toggle-row">
                      <span style={{ fontWeight: 500 }}>Public sharing</span>
                      <label className="mc-share-switch">
                        <input
                          type="checkbox"
                          checked={shareSettings?.share_enabled || false}
                          onChange={(e) => toggleSharing(e.target.checked)}
                          disabled={shareLoading}
                        />
                        <span className="mc-share-slider" />
                      </label>
                    </div>

                    {shareSettings?.share_enabled && (
                      <>
                        {/* Link display + copy */}
                        <div className="mc-share-link-row">
                          <input
                            type="text"
                            readOnly
                            value={getFullShareUrl()}
                            className="mc-share-link-input"
                            onClick={(e) => e.target.select()}
                          />
                          <button
                            className={`mc-btn mc-btn-primary mc-share-copy-btn ${shareCopied ? 'mc-copied' : ''}`}
                            onClick={copyShareLink}
                          >
                            {shareCopied ? '✓ Copied!' : '📋 Copy'}
                          </button>
                        </div>

                        {/* Social share buttons */}
                        <div className="mc-share-social-row">
                          <button className="mc-share-social-btn mc-share-facebook" onClick={() => shareToSocial('facebook')} title="Share on Facebook">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                          </button>
                          <button className="mc-share-social-btn mc-share-twitter" onClick={() => shareToSocial('twitter')} title="Share on X / Twitter">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                          </button>
                          <button className="mc-share-social-btn mc-share-reddit" onClick={() => shareToSocial('reddit')} title="Share on Reddit">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
                          </button>
                          <button className="mc-share-social-btn mc-share-email" onClick={() => shareToSocial('email')} title="Share via email">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                          </button>
                        </div>

                        {/* Preview + Regenerate */}
                        <div className="mc-share-footer">
                          <Link to={shareSettings.share_url} target="_blank" className="mc-share-preview-link">
                            👁️ Preview public page
                          </Link>
                          <button className="mc-share-regen-btn" onClick={regenerateShareLink} disabled={shareLoading}>
                            🔄 New link
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCollectionScreen;